<template>
    <app-layout>
        <page-header :title="trans('users.overview.title')" title-in-app-header>
            <div class="mt-3 flex flex-wrap items-start gap-3">
                <filter-bar v-model="queryParams" :config="filterBarConfig" class="min-w-0 flex-1" />
                <jet-button
                    v-if="hasAnyPermission('user.create')"
                    class="shrink-0"
                    type="button"
                    variant="secondary"
                    @click="$inertia.visit(route('users.create'))"
                >
                    {{ trans('users.overview.action.add_user') }}
                </jet-button>
            </div>
        </page-header>
        <data-table
            :actions="actions"
            :columns="fields"
            :items="usersStore.paginator"
            :no-items-view="{ noItems: trans('users.overview.results.empty') }"
            :processing-items="usersStore.processingItems"
            :query-params="queryParams"
            action-classes="align-top"
            @row-clicked="usersStore.showDetails"
        >
            <template #name="{ item }">
                <td class="align-top">
                    <div class="align-center flex justify-between">
                        <div class="truncate">{{ item.name }}</div>
                        <icon
                            v-if="item.meta?.data.nameIsAmbiguous"
                            v-c-tooltip="{
                                content: 'Ambiguous name',
                            }"
                            class="text-warning"
                            name="cil-warning"
                        />
                    </div>
                    <small v-if="item.lastLoginAt !== undefined" class="text-muted"
                        >{{ trans('users.overview.last_login') }}
                        <span v-if="!!item.lastLoginAt" class="text-nowrap">{{ $filters.dateTimeMedium(item.lastLoginAt, currentLocale) }}</span>
                        <span v-else>{{ trans('general.not_available') }}</span>
                    </small>
                </td>
            </template>

            <template #email="{ item }">
                <td class="align-top">
                    <email-link :email="item.email" />
                </td>
            </template>

            <template #role="{ item }">
                <td class="align-top">
                    <RoleBadge v-for="role in item.roles" :key="role.id" :role="role" />
                </td>
            </template>

            <template #status="{ item }">
                <td class="align-top">
                    <badge :color="getStatusBadge(item.status)">
                        {{ item.status?.label.toUpperCase() }}
                    </badge>
                </td>
            </template>

            <template #locations="{ item }">
                <td class="text-start align-top">
                    <ul v-if="getLocationDisplayObjects(item).length > 0" class="list-unstyled !mb-0 flex flex-col gap-1">
                        <li
                            v-for="locationDisplay in getLocationDisplayObjects(item)"
                            :key="locationDisplay.id"
                            class="flex w-full items-center justify-start"
                        >
                            <div class="flex h-full items-center text-gray-text">{{ locationDisplay.displayName }}</div>
                            <span
                                v-if="locationDisplay.countDisplay"
                                class="mx-1 shrink-0 rounded-full bg-secondary px-2 py-1 text-xs font-medium text-gray-text"
                                >{{ locationDisplay.countDisplay }}</span
                            >
                        </li>
                    </ul>
                    <div v-else>
                        {{ trans('users.show.airports.empty') }}
                    </div>
                </td>
            </template>
        </data-table>
    </app-layout>
</template>
<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import JetButton from '@/Jetstream/Button.vue';
import RoleBadge from '@/Components/RoleBadge.vue';
import EmailLink from '@/Components/EmailLink.vue';
import DataTable from '@/Components/DataTable.vue';
import Badge from '@/Components/Badge.vue';
import PageHeader from '@/Components/PageHeader.vue';
import FilterBar from '@/Components/FilterBar.vue';
import { computed, ref, watch } from 'vue';
import { buildLocationDisplayObjects } from '@/composables/useHierarchicalLocationDisplay.ts';
import { trans } from '@/mixins/i18n';
import { usePage } from '@inertiajs/vue3';
import { useUsersStore } from '@/stores/users';

const page = usePage();
const usersStore = useUsersStore();
const queryParams = ref(null);
const filterBarConfig = computed(() => ({
    route: route('users.index'),
    routeOptions: { only: ['users'] },
    defaultSort: { column: 'name', asc: true },
    filters: [{ type: 'search', key: 'search', placeholder: trans('users.overview.filter.placeholder') }],
}));

const fields = [
    { key: 'name', label: trans('users.overview.cell.name') },
    { key: 'email', label: trans('users.overview.cell.email') },
    { key: 'role', label: trans('users.overview.cell.role') },
    { key: 'status', label: trans('users.overview.cell.status') },
    { key: 'locations', label: trans('users.overview.cell.locations') },
];

const users = computed(() => page.props.users);

watch(
    users,
    (val) => {
        usersStore.setPaginatorFromUsersProp(val);
    },
    { immediate: true },
);
const hierarchicalLocations = computed(() => page.props.hierarchicalLocations || []);

const getStatusBadge = (status) => {
    switch (status?.value) {
        case 'active':
            return 'success';
        case 'invited':
            return 'info';
        default:
            return 'danger';
    }
};

const actions = [
    {
        id: 'edit',
        condition: (item) => !!item.links?.edit,
        tooltip: trans('users.overview.tooltip.edit_user'),
        type: 'primary',
        click: usersStore.editUser,
        icon: 'cil-pencil',
    },
    {
        id: 'invite',
        condition: (item) => !!item.links?.resendInvitation,
        tooltip: trans('users.overview.tooltip.resend_invitation'),
        type: 'primary',
        click: usersStore.inviteUser,
        icon: 'cil-paper-plane',
    },
    {
        id: 'revoke',
        condition: (item) => !!item.links?.revoke,
        tooltip: trans('users.overview.tooltip.revoke_access'),
        type: 'primary',
        click: usersStore.revokeAccess,
        icon: 'cil-lock-locked',
    },
    {
        id: 'restore',
        condition: (item) => !!item.links?.restoreAccess,
        tooltip: trans('users.overview.tooltip.restore_access'),
        type: 'primary',
        click: usersStore.restoreAccess,
        icon: 'cil-lock-unlocked',
    },
    {
        id: 'impersonate',
        condition: (item) => !!item.links?.impersonate,
        tooltip: trans('users.overview.tooltip.impersonate'),
        type: 'warning',
        click: usersStore.impersonate,
        icon: 'cil-user',
    },
    {
        id: 'delete',
        condition: (item) => !!item.links?.delete,
        tooltip: trans('users.overview.tooltip.delete_user'),
        type: 'danger',
        click: usersStore.deleteUser,
        icon: 'cil-trash',
    },
];

const getLocationDisplayObjects = (user) => {
    const userLocationIds = user.locations?.map((location) => location.id) || [];
    return buildLocationDisplayObjects(hierarchicalLocations.value, userLocationIds, { includeMetadata: true });
};
</script>
